import React from 'react';
import {useTranslation} from 'react-i18next';
import {ArrowsClockwise, Warning} from '../../icons';
import {MERCADO_ADS_CHANNEL} from '../../channels/channels';
import {historyNoticeText} from '../../channels/mercado_ads/mercadoAds';

/**
 * El progreso del histórico de UNA cuenta, dentro de su fila del bloque Plataformas.
 *
 * Al conectar una fuente nueva, Dashbo trae los datos **mes a mes desde hoy hacia atrás** hasta
 * enero de 2024. Para una tienda de volumen alto eso tarda HORAS, y hasta esta card la fila no
 * decía absolutamente nada: el usuario no sabía si estaba andando, cuánto faltaba, ni desde qué
 * fecha ya podía confiar en los números (card 2Un2oFMO).
 *
 * ## Por qué NO es un porcentaje
 *
 * El "preparando X%" de la pantalla clásica cuenta filas de bookkeeping de BigQuery, no datos: en
 * el caso que originó la card mostraba `100%` con nueve días de datos reales. Acá el progreso sale
 * del propio proceso que trae los meses, y se dice en la unidad en la que el usuario piensa —meses—
 * junto con lo único que de verdad le sirve mientras espera: **desde cuándo ya puede mirar**.
 *
 * Eso último lo habilita que el proceso vaya en orden descendente, que es deliberado: lo reciente
 * queda disponible primero. "Ya hay datos desde junio" es información accionable; "43%" no lo es.
 *
 * ## Dos estados, y por qué el segundo existe
 *
 * - **Preparando**: la cadena está viva. La barra avanza sola (`PlatformsCard` refresca el progreso
 *   mientras haya alguna cuenta en este estado).
 * - **Interrumpida**: la cadena se cortó antes de llegar al final. Sin este estado, una cuenta cuyo
 *   proceso murió mostraría "preparando" para siempre — una barra que no avanza nunca es peor que
 *   no mostrar nada. No promete cuándo se reanuda: hay una red de seguridad que lo reintenta, pero
 *   decide con la misma fuente de bookkeeping que puede mentir, así que garantizarlo sería otra vez
 *   afirmar más de lo que se sabe.
 *
 * Cuando el histórico termina, el backend deja de mandar el bloque y esta fila **desaparece sola**:
 * no hay un estado "listo" que después haya que limpiar.
 *
 * Mercado Ads no llega a enero de 2024: Mercado Libre guarda sólo 90 días, y la fila lo dice para que
 * nadie espere meses que no existen.
 */
export function HistoricalSyncRow({sync, channel = null}) {
    const {t, i18n} = useTranslation();

    if (!sync) return null;

    const stalled = sync.state === 'STALLED';
    const total = Number(sync.months_total) || 0;
    const done = Number(sync.months_done) || 0;
    // El tope en 99 es a propósito: mientras esta fila se dibuja el proceso NO terminó, y una barra
    // llena diciendo "preparando" es la misma contradicción que el 100% del porcentaje clásico.
    const percentage = total > 0 ? Math.min(Math.round((done / total) * 100), 99) : 0;

    const monthLabel = formatMonth(sync.current_month, i18n.language);
    const dataFromLabel = formatDay(sync.data_from, i18n.language);

    const headline = stalled
        ? t('client_panel.sync_stalled_title', 'Se interrumpió la preparación del histórico')
        : t('client_panel.sync_running_title', 'Preparando el histórico');

    /**
     * El pie: primero lo que el usuario PUEDE hacer (mirar los datos que ya están), después dónde
     * va el proceso. Ese orden no es cosmético — es la diferencia entre una espera opaca y una en
     * la que ya se puede trabajar.
     */
    const renderDetail = () => {
        const where = stalled
            ? t('client_panel.sync_stopped_at', 'Se detuvo en {{month}}.',
                {month: monthLabel, interpolation: {escapeValue: false}})
            : t('client_panel.sync_working_on', 'Trayendo {{month}}.',
                {month: monthLabel, interpolation: {escapeValue: false}});

        if (!dataFromLabel) {
            // Todavía no terminó ni un mes: decir "ya hay datos desde…" sería mentir, y un guion
            // vacío no explica nada. Se dice lo único cierto.
            return (
                <>
                    <span className="pnl-sync__pending">
                        {t('client_panel.sync_no_data_yet', 'Todavía no hay datos para consultar.')}
                    </span>
                    {' '}{where}
                </>
            );
        }

        return (
            <>
                <span className="pnl-sync__ready">
                    {t('client_panel.sync_data_from', 'Ya hay datos desde el {{date}}.',
                        {date: dataFromLabel, interpolation: {escapeValue: false}})}
                </span>
                {' '}{where}
            </>
        );
    };

    return (
        // Dos elementos y no uno: el SLOT ocupa el renglón entero (`flex-basis: 100%`, que es lo que
        // fuerza el salto de línea dentro de la fila de la cuenta) y la CAJA lleva el ancho máximo.
        // Puestos en el mismo elemento se pelean: el `max-width` achica el tamaño hipotético con el
        // que flexbox decide el wrap, así que el bloque dejaba de bajar de renglón y se acomodaba a
        // la derecha de la fila —a ~700 px del nombre de su propia cuenta— por el `space-between`.
        <span className="pnl-sync-slot">
        {/* `role="status"` y no un `aria-live` sobre la barra: el texto de este bloque cambia SOLO
            cada 60 s y es la única superficie del Panel que se refresca sin que nadie la toque, así
            que quien usa lector de pantalla no se enteraba nunca del avance. La barra, en cambio,
            sigue `aria-hidden`: no aporta nada que el texto no diga. */}
        <span className={`pnl-sync${stalled ? ' -stalled' : ''}`} role="status">
            <span className="pnl-sync__head">
                {stalled
                    ? <Warning size={12} weight="fill"/>
                    // El ícono gira mientras se prepara. Entre tick y tick pasan 60 s en los que
                    // nada se mueve, y en un proceso que dura horas eso es indistinguible de algo
                    // congelado — que es justo lo que el estado «interrumpido» quiere poder decir
                    // por contraste. La animación se apaga con `prefers-reduced-motion` (CSS).
                    : <ArrowsClockwise size={12} weight="bold" className="pnl-sync__spin"/>}
                <span className="pnl-sync__title">{headline}</span>
                {total > 0 && (
                    <span className="pnl-sync__count">
                        {t('client_panel.sync_months', '{{done}} de {{total}} meses',
                            {done: done, total: total})}
                    </span>
                )}
            </span>

            <span className="pnl-sync__detail">{renderDetail()}</span>
            {channel === MERCADO_ADS_CHANNEL && (
                <span className="pnl-sync__detail" data-testid="pnl-sync-mercado-ads-notice">
                    {historyNoticeText(t, i18n.language)}
                </span>
            )}

            {/* La barra CIERRA el bloque, no separa el título del detalle.
                Entre los dos —que es donde nace cualquier barra de progreso— sus 3px a ancho
                completo se leían como un SUBRAYADO del encabezado: se vio renderizando la pantalla,
                y subirle el aire de 3 a 7px no alcanzó. Abajo de todo no tiene con qué confundirse,
                y además deja el orden narrativo que el bloque quiere: qué está pasando → desde
                cuándo podés mirar → cuánto falta.

                Es decorativa: todo lo que dice está escrito arriba en palabras, así que se esconde
                del lector de pantalla en vez de duplicarle el dato. */}
            {total > 0 && (
                <span className="pnl-sync__bar" aria-hidden="true">
                    <span className="pnl-sync__fill" style={{width: `${percentage}%`}}/>
                </span>
            )}
        </span>
        </span>
    );
}

/**
 * `'2025-02'` → `'feb 2025'`, en el idioma de la interfaz.
 *
 * Se construye la fecha con los componentes SUELTOS y nunca con `new Date('2025-02')`: ese formato
 * se interpreta como medianoche UTC, y en cualquier huso al oeste —el nuestro— cae en el mes
 * ANTERIOR. Es el mismo desfasaje que ya mordió en las fechas de la Memoria.
 */
function formatMonth(isoMonth, language) {
    const match = /^(\d{4})-(\d{2})$/.exec(String(isoMonth || ''));
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
    return date.toLocaleDateString(language || 'es', {month: 'short', year: 'numeric'});
}

/** `'2025-03-01'` → `'1 mar 2025'`. Mismo cuidado con el huso que `formatMonth`. */
function formatDay(isoDay, language) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoDay || ''));
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return date.toLocaleDateString(language || 'es',
        {day: 'numeric', month: 'short', year: 'numeric'});
}
